/**
 * 设备指纹工具
 * 生成基于浏览器和设备特征的唯一指纹
 * 用于跨设备检测和加密密钥生成
 */

/**
 * 生成设备指纹
 * 基于多个浏览器和设备特征生成唯一标识
 * 跨设备或跨浏览器时，指纹会不同
 * 
 * @returns {string} 设备指纹（SHA256哈希值）
 */
export function generateDeviceFingerprint() {
  try {
    const components = []
    
    // 1. 用户代理字符串
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      components.push('ua:' + navigator.userAgent)
    }
    
    // 2. 屏幕分辨率和颜色深度
    if (typeof screen !== 'undefined') {
      components.push('screen:' + screen.width + 'x' + screen.height + 'x' + screen.colorDepth)
    }
    
    // 3. 时区
    if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        components.push('tz:' + timezone)
      } catch (e) {
        // 忽略时区获取错误
      }
    }
    
    // 4. 语言设置
    if (typeof navigator !== 'undefined') {
      if (navigator.language) {
        components.push('lang:' + navigator.language)
      }
      if (navigator.languages && navigator.languages.length > 0) {
        components.push('langs:' + navigator.languages.join(','))
      }
    }
    
    // 5. 平台信息
    if (typeof navigator !== 'undefined' && navigator.platform) {
      components.push('platform:' + navigator.platform)
    }
    
    // 6. 硬件并发数（CPU核心数）
    if (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) {
      components.push('cores:' + navigator.hardwareConcurrency)
    }
    
    // 7. Canvas 指纹（如果可用）
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.textBaseline = 'top'
        ctx.font = '14px Arial'
        ctx.fillText('DeviceFingerprint@ZX', 2, 2)
        const canvasFingerprint = canvas.toDataURL()
        // 只取前100个字符，避免过长
        components.push('canvas:' + canvasFingerprint.substring(0, 100))
      }
    } catch (e) {
      // Canvas 不可用时忽略
    }
    
    // 8. WebGL 指纹（如果可用）
    try {
      const canvas = document.createElement('canvas')
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
      if (gl) {
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
        if (debugInfo) {
          const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
          const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
          components.push('webgl:' + vendor + '|' + renderer)
        }
      }
    } catch (e) {
      // WebGL 不可用时忽略
    }
    
    // 9. 本地存储可用性
    try {
      const testKey = '__fingerprint_test__'
      localStorage.setItem(testKey, '1')
      localStorage.removeItem(testKey)
      components.push('storage:available')
    } catch (e) {
      components.push('storage:unavailable')
    }
    
    // 10. 浏览器窗口大小（如果可用）
    if (typeof window !== 'undefined') {
      components.push('window:' + window.innerWidth + 'x' + window.innerHeight)
    }
    
    // 组合所有组件并生成哈希
    const combined = components.join('|')
    
    // 使用 SHA256 生成指纹
    let fingerprint
    try {
      // 使用 crypto-js
      const CryptoJS = require('crypto-js')
      if (CryptoJS && CryptoJS.SHA256) {
        fingerprint = CryptoJS.SHA256(combined).toString()
      } else {
        throw new Error('CryptoJS.SHA256 不可用')
      }
    } catch (e) {
      // 如果 crypto-js 不可用，使用简单的哈希函数
      console.warn('crypto-js 不可用，使用简单哈希:', e)
      fingerprint = simpleHash(combined)
    }
    
    return fingerprint
  } catch (error) {
    console.error('生成设备指纹失败:', error)
    // 如果生成失败，使用时间戳和随机数作为后备方案
    return simpleHash(Date.now().toString() + Math.random().toString())
  }
}

/**
 * 简单的哈希函数（后备方案）
 * @param {string} str 输入字符串
 * @returns {string} 哈希值
 */
function simpleHash(str) {
  let hash = 0
  if (str.length === 0) return hash.toString()
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // 转换为32位整数
  }
  return Math.abs(hash).toString(36)
}

/**
 * 获取或生成设备指纹（带缓存）
 * @returns {string} 设备指纹
 */
let cachedFingerprint = null

export function getDeviceFingerprint() {
  if (cachedFingerprint) {
    return cachedFingerprint
  }
  
  try {
    // 尝试从 localStorage 获取缓存的指纹
    const stored = localStorage.getItem('device_fingerprint')
    if (stored) {
      cachedFingerprint = stored
      return stored
    }
  } catch (e) {
    // 如果无法访问 localStorage，继续生成新指纹
  }
  
  // 生成新指纹
  cachedFingerprint = generateDeviceFingerprint()
  
  // 尝试保存到 localStorage（用于后续使用）
  try {
    localStorage.setItem('device_fingerprint', cachedFingerprint)
  } catch (e) {
    // 如果无法保存，忽略错误
  }
  
  return cachedFingerprint
}

/**
 * 清除设备指纹缓存
 */
export function clearDeviceFingerprint() {
  cachedFingerprint = null
  try {
    localStorage.removeItem('device_fingerprint')
  } catch (e) {
    // 忽略错误
  }
}

